import { sortBy } from 'lodash'
import { ReactNode } from 'react'
import {
  CallParticipantTypeEnum,
  callParticipantTypesOurUser,
  CallTypeToJobTypeEnum,
  ICall,
  ITranscriptMessage,
} from 'features/calls/types'
import { IClaim } from 'features/claims/types'
import { IUser } from 'features/auth/types/authTypes'
import { dateDiffToHHMMSS } from 'utils/date'
import { isOutboundUser } from 'utils/Utils'

type User = IUser | null | undefined

const eventTypeToMessage: Map<string, string> = new Map<string, string>([
  ['HumanTakeOverFromBrowser', '{0} took over the call at {2}'],
  ['HumanAgentHoldStart', '{0} on hold with {3} at {2}'],
  ['HumanAgentHoldEnd', '{0} heard a {3} agent answer at {2}'],
  ['DialedCallBusy', '{1} phone was busy at {2}'],
  ['DialedCallCompleted', 'Call with {0} ended at {2}'],
  ['DialedCallFailed', '{1} call failed at {2}'],
  ['DialedCallNoAnswer', '{1} call failed unanswered at {2}'],
  ['DialedCallRinging', '{1} phone started ringing at {2}'],
  ['DialedCallInProgress', '{0} answered at {2}'],
  ['ParticipantStoppedConversation', '{0} hung up at {2}'],
  ['TakeOverFromBrowserFailed', 'Failed to take over the call at {2}'],
])

export const getUserTitle = (call: ICall, user?: User): string => {
  return call.jobType === CallTypeToJobTypeEnum.diagnosticHeadset
    ? `${user?.given_name} ${user?.family_name}`
    : `${call.initiatorUser?.firstName} ${call.initiatorUser?.lastName}`
}

export const getParticipantTypeToTitleMapping = (call: ICall, claim: IClaim, user?: User) => {
  const userTitle = getUserTitle(call, user)

  return {
    [CallParticipantTypeEnum.foreignPhoneParticipant]: claim.insurerName || '',
    [CallParticipantTypeEnum.twilioParticipant]: claim.insurerName || '',
    [CallParticipantTypeEnum.domesticPhoneParticipant]: userTitle,
    [CallParticipantTypeEnum.browserParticipant]: userTitle,
    [CallParticipantTypeEnum.AIAgentParticipant]: 'AI Agent',
    [CallParticipantTypeEnum.HCAgentParticipant]: 'HC Agent',
    [CallParticipantTypeEnum.transcriptEventDetectorParticipant]: 'AI Agent',
    [CallParticipantTypeEnum.diagnosticParticipant]: 'Diagnostic Agent',
    [CallParticipantTypeEnum.jobSchedulerParticipant]: 'Call Scheduler',
    [CallParticipantTypeEnum.DoubleTranscription]: 'Extraction Transcript',
  }
}

export const formatTranscriptMessage = (
  message: ITranscriptMessage,
  call: ICall,
  claim: IClaim,
  callStartTime: string,
  user?: User,
): { messageText: string; participantTitle: string; timestamp: string; eventType: boolean } => {
  const participantTypeToTitleMapping = getParticipantTypeToTitleMapping(call, claim, user)

  let messageData = ''
  try {
    messageData = JSON.parse(message.message)
  } catch (e) {
    // message is not json string object, do nothing
  }

  let messageText = message.message
  let eventType = false
  const participantTitle =
    participantTypeToTitleMapping[message.participantType as keyof typeof participantTypeToTitleMapping] ||
    'Unknown participant'

  // Check if this is an event type message
  if (typeof messageData === 'object' && messageData['eventType']) {
    const template = eventTypeToMessage.get(messageData['eventType'])
    const youYour =
      callParticipantTypesOurUser.includes(message.participantType) &&
      user?.given_name + ' ' + user?.family_name === call.initiatorUser?.firstName + ' ' + call.initiatorUser?.lastName
    const theyTheir = participantTitle

    if (template) {
      eventType = true
      const args = [
        youYour ? 'You' : theyTheir,
        youYour ? 'Your' : theyTheir,
        `${dateDiffToHHMMSS(new Date(message.created).toISOString(), new Date(callStartTime).toISOString())}`,
        'payer',
      ]
      messageText = template.replace(/{(\d+)}/g, function (match, number) {
        return typeof args[number] != 'undefined' ? args[number] : match
      })
    }
  }

  const timestamp = dateDiffToHHMMSS(
    new Date(message.created).toISOString(),
    new Date(callStartTime).toISOString(),
    true,
  ) || ''

  return { messageText, participantTitle, timestamp, eventType }
}

export interface IFormattedMessage {
  Icon: JSX.Element
  title: string
  message: string
  eventType: boolean
  id: string
}

export const formatTranscriptMessages = (
  transcript: ITranscriptMessage[],
  call: ICall,
  claim: IClaim,
  callStartTime: string,
  user: User,
  createIconForParticipant: (participantType: CallParticipantTypeEnum) => JSX.Element,
): IFormattedMessage[] => {
  const messages = sortBy(transcript, 'created').map((message) => {
    // Skip DoubleTranscription for non-Outbound users
    if (!isOutboundUser(user!)) {
      if (message.participantType === CallParticipantTypeEnum.DoubleTranscription) {
        return null
      }
    }

    const { messageText, participantTitle, timestamp, eventType } = formatTranscriptMessage(
      message,
      call,
      claim,
      callStartTime,
      user,
    )

    return {
      Icon: createIconForParticipant(message.participantType),
      title: `${participantTitle} · ${timestamp}`,
      message: messageText,
      eventType: eventType,
      id: message.id,
    }
  })

  return messages.filter(Boolean) as IFormattedMessage[]
}
